import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;
export const PUBLIC_API = `${BACKEND}/api/public`;
export const ADMIN_API = `${BACKEND}/api/admin`;

const TOKEN_KEY = "2high_admin_token";

export const auth = {
  get token() { return localStorage.getItem(TOKEN_KEY); },
  set token(v) { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); },
  isLoggedIn() { return !!localStorage.getItem(TOKEN_KEY); },
  logout() { localStorage.removeItem(TOKEN_KEY); window.location.href = "/owner-cabinet/login"; },
};

const client = axios.create();
client.interceptors.request.use((cfg) => {
  if (auth.token && cfg.url && cfg.url.startsWith(ADMIN_API)) {
    cfg.headers.Authorization = `Bearer ${auth.token}`;
  }
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401 && auth.token) auth.logout();
    return Promise.reject(err);
  }
);

export const api = {
  // ---- Auth ----
  login: (email, password) => client.post(`${API}/auth/login`, { email, password }).then((r) => r.data),
  me: () => client.get(`${ADMIN_API}/auth/me`).then((r) => r.data),
  changePassword: (current_password, new_password) =>
    client.post(`${ADMIN_API}/auth/change-password`, { current_password, new_password }).then((r) => r.data),

  // ---- Content ----
  getContent: () => client.get(`${ADMIN_API}/content`).then((r) => r.data),
  saveContent: (payload) => client.put(`${ADMIN_API}/content`, payload).then((r) => r.data),

  // ---- Categories ----
  getCategories: () => client.get(`${ADMIN_API}/categories`).then((r) => r.data),
  saveCategories: (cats) => client.put(`${ADMIN_API}/categories`, cats).then((r) => r.data),

  // ---- Products ----
  listProducts: (includeDeleted = false) => client.get(`${ADMIN_API}/products?include_deleted=${includeDeleted}`).then((r) => r.data),
  createProduct: (p) => client.post(`${ADMIN_API}/products`, p).then((r) => r.data),
  updateProduct: (id, p) => client.put(`${ADMIN_API}/products/${id}`, p).then((r) => r.data),
  deleteProduct: (id) => client.delete(`${ADMIN_API}/products/${id}`).then((r) => r.data),
  restoreProduct: (id) => client.post(`${ADMIN_API}/products/${id}/restore`).then((r) => r.data),
  duplicateProduct: (id) => client.post(`${ADMIN_API}/products/${id}/duplicate`).then((r) => r.data),
  reorderProducts: (items) => client.post(`${ADMIN_API}/products/reorder`, items).then((r) => r.data),
  bulkAvailability: (ids, availability) =>
    client.post(`${ADMIN_API}/products/bulk-availability`, { ids, availability }).then((r) => r.data),

  // ---- Undo ----
  undo: () => client.post(`${ADMIN_API}/undo`).then((r) => r.data),

  // ---- Testimonials ----
  listTestimonials: (includeDeleted = false) => client.get(`${ADMIN_API}/testimonials?include_deleted=${includeDeleted}`).then((r) => r.data),
  createTestimonial: (t) => client.post(`${ADMIN_API}/testimonials`, t).then((r) => r.data),
  updateTestimonial: (id, t) => client.put(`${ADMIN_API}/testimonials/${id}`, t).then((r) => r.data),
  deleteTestimonial: (id) => client.delete(`${ADMIN_API}/testimonials/${id}`).then((r) => r.data),
  restoreTestimonial: (id) => client.post(`${ADMIN_API}/testimonials/${id}/restore`).then((r) => r.data),
  reorderTestimonials: (items) => client.post(`${ADMIN_API}/testimonials/reorder`, items).then((r) => r.data),

  // ---- Landed ----
  listLanded: (includeDeleted = false) => client.get(`${ADMIN_API}/landed?include_deleted=${includeDeleted}`).then((r) => r.data),
  createLanded: (l) => client.post(`${ADMIN_API}/landed`, l).then((r) => r.data),
  updateLanded: (id, l) => client.put(`${ADMIN_API}/landed/${id}`, l).then((r) => r.data),
  deleteLanded: (id) => client.delete(`${ADMIN_API}/landed/${id}`).then((r) => r.data),
  restoreLanded: (id) => client.post(`${ADMIN_API}/landed/${id}/restore`).then((r) => r.data),
  reorderLanded: (items) => client.post(`${ADMIN_API}/landed/reorder`, items).then((r) => r.data),

  // ---- Inquiries ----
  listInquiries: (status) =>
    client.get(`${ADMIN_API}/inquiries${status ? `?status=${status}` : ""}`).then((r) => r.data),
  updateInquiry: (id, data) => client.put(`${ADMIN_API}/inquiries/${id}`, data).then((r) => r.data),
  deleteInquiry: (id) => client.delete(`${ADMIN_API}/inquiries/${id}`).then((r) => r.data),

  // ---- Draft Orders (Drop Builder cart submissions) ----
  listDraftOrders: (status) =>
    client.get(`${ADMIN_API}/draft-orders${status ? `?status=${status}` : ""}`).then((r) => r.data),
  draftOrderStats: () => client.get(`${ADMIN_API}/draft-orders/stats`).then((r) => r.data),
  updateDraftOrder: (id, data) => client.put(`${ADMIN_API}/draft-orders/${id}`, data).then((r) => r.data),
  viewDraftOrder:  (id) => client.post(`${ADMIN_API}/draft-orders/${id}/view`).then((r) => r.data),
  deleteDraftOrder: (id) => client.delete(`${ADMIN_API}/draft-orders/${id}`).then((r) => r.data),

  // ---- Wheel marketing list ----
  listWheelSpins: () => client.get(`${ADMIN_API}/wheel/spins`).then((r) => r.data),
  deleteWheelSpin: (id) => client.delete(`${ADMIN_API}/wheel/spins/${id}`).then((r) => r.data),
  markWheelClaimed: (id) => client.put(`${ADMIN_API}/wheel/spins/${id}/claimed`).then((r) => r.data),
  resetWheelCampaign: () => client.post(`${ADMIN_API}/wheel/reset-campaign`).then((r) => r.data),

  // ---- Backup / Restore ----
  exportBackup: () => client.get(`${ADMIN_API}/backup`).then((r) => r.data),
  restoreBackup: (payload) => client.post(`${ADMIN_API}/restore`, payload).then((r) => r.data),

  // ---- Referral analytics ----
  referralStats: (days = 30) => client.get(`${ADMIN_API}/referrals?days=${days}`).then((r) => r.data),
  listReferralLinks: () => client.get(`${ADMIN_API}/referral-links`).then((r) => r.data),
  createReferralLink: (data) => client.post(`${ADMIN_API}/referral-links`, data).then((r) => r.data),
  updateReferralLink: (id, data) => client.put(`${ADMIN_API}/referral-links/${id}`, data).then((r) => r.data),
  deleteReferralLink: (id) => client.delete(`${ADMIN_API}/referral-links/${id}`).then((r) => r.data),

  // ---- Media ----
  listMedia: () => client.get(`${ADMIN_API}/media`).then((r) => r.data),
  uploadMedia: async (file, onProgress) => {
    // Retry up to 2 times on transient failures (5xx, network, ingress 404).
    // This makes bulk-upload flows resilient when 10+ files race through the
    // Kubernetes ingress at once.
    const RETRIES = 2;
    let lastErr;
    for (let attempt = 0; attempt <= RETRIES; attempt++) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await client.post(`${ADMIN_API}/media/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        return res.data;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        // Retry on: network error (no response), 404 from ingress, 5xx errors
        const retriable = !err.response || status === 404 || (status >= 500 && status < 600);
        if (!retriable || attempt === RETRIES) break;
        // Exponential backoff: 600ms, 1800ms
        await new Promise(r => setTimeout(r, 600 * Math.pow(3, attempt)));
      }
    }
    throw lastErr;
  },
  deleteMedia: (id) => client.delete(`${ADMIN_API}/media/${id}`).then((r) => r.data),

  // ---- Audit + Analytics ----
  getAudit: (limit = 200) => client.get(`${ADMIN_API}/audit?limit=${limit}`).then((r) => r.data),
  getAnalytics: () => client.get(`${ADMIN_API}/analytics`).then((r) => r.data),
};

// Resolve a media URL (relative path from API to absolute)
export const mediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/")) return `${BACKEND}${path}`;
  return path;
};
